<script setup lang="ts">
import { formatKey, formatWaitDuration, truncate } from '@shared/utils'
import type { VaspWalletListItem } from '../types/cryptoWalletTypes'
import { cryptoWalletStatusColor } from '../constants/cryptoWalletStatus'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Tooltip from '@uiKit/elements/Tooltip.vue'
import DataTableV2, { type TableColumn, type TableAction } from '@uiKit/components/DataTableV2.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'

const NAME_TRUNCATE_AT = 20
const ADDRESS_TRUNCATE_AT = 18

defineProps<{
  rows: VaspWalletListItem[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  search: string
}>()

const emit = defineEmits<{
  (e: 'update:search', value: string): void
  (e: 'update:page', value: number): void
  (e: 'row-click', row: VaspWalletListItem): void
  (e: 'view-details', row: VaspWalletListItem): void
  (e: 'sync', row: VaspWalletListItem): void
}>()

// "17d 4h ago" — duration since the wallet was created, with an "ago" suffix.
function formatCreatedAgo(iso: string | null | undefined): string {
  const duration = formatWaitDuration(iso)
  if (duration === '—' || duration === 'just now') return duration
  return `${duration} ago`
}

// Only a wallet Ctrl-Alt actually issued can be reconciled against it, and
// `vaspReferenceId` is exactly what says so — it is the id the sync addresses the
// provider by, and admin-service refuses with 409 VASP_WALLET_NOT_SYNCABLE when
// it is missing. Hide the action rather than letting the operator find that out
// by clicking it.
//
// Deliberately NOT gated on validationMethod: that field is provenance, and the
// current mint path writes `xrpl_custody` rather than `vasp`. Gating on it hid
// Sync from every wallet minted since — registered at Ctrl-Alt, reference id
// present, action gone.
function canSync(row: VaspWalletListItem): boolean {
  return Boolean(row.vaspReferenceId)
}

const rowActions: TableAction<VaspWalletListItem>[] = [
  {
    label: 'View details',
    icon: 'eye',
    onClick: (row) => emit('view-details', row),
  },
  {
    label: 'Sync',
    icon: 'refresh',
    hidden: (row) => !canSync(row),
    onClick: (row) => emit('sync', row),
  },
]

const columns: TableColumn[] = [
  { key: 'investor', label: 'INVESTOR', width: '200px' },
  { key: 'walletAddress', label: 'WALLET ADDRESS', width: '190px' },
  { key: 'blockchainNetwork', label: 'NETWORK', width: '110px' },
  // No provenance column: every wallet this flow mints is broker custody, so it
  // was the same value on every row — a column that never varies is noise, and
  // sat next to VALIDATION STATUS looking like a second status. The field is
  // still on the drawer, where a legacy `vasp` or `manual` row can be told apart.
  { key: 'walletStatus', label: 'VALIDATION STATUS', width: '160px' },
  { key: 'createdAt', label: 'CREATED AT', width: '150px' },
]

function investorFullName(row: VaspWalletListItem): string {
  return `${row.firstName} ${row.lastName}`.trim()
}

function investorDisplayName(row: VaspWalletListItem): string {
  const name = investorFullName(row)
  if (!name) return '—'
  return name.length > NAME_TRUNCATE_AT ? `${name.slice(0, NAME_TRUNCATE_AT)}…` : name
}

function investorNameTooltip(row: VaspWalletListItem): string {
  const name = investorFullName(row)
  return name.length > NAME_TRUNCATE_AT ? name : ''
}
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    title="VAISP Onboarding Queue"
    :subtitle="`${total} wallets created via VAISP`"
    row-key="id"
    search-placeholder="Search name, email, address..."
    :local-search="false"
    :local-pagination="false"
    :page="page"
    :page-size="pageSize"
    :total="total"
    :search="search"
    clickable
    :actions="rowActions"
    empty-title="No wallets in this queue"
    empty-subtitle="Wallets created when a VAISP registration is approved will appear here."
    @update:search="emit('update:search', $event)"
    @update:page="emit('update:page', $event)"
    @row-click="emit('row-click', $event as VaspWalletListItem)"
  >
    <!-- INVESTOR -->
    <template #cell-investor="{ row }">
      <Box flex direction="col">
        <Tooltip :content="investorNameTooltip(row as VaspWalletListItem)" position="top">
          <Text type="body" weight="semibold">
            {{ investorDisplayName(row as VaspWalletListItem) }}
          </Text>
        </Tooltip>
        <Text type="caption" color="muted">{{ (row as VaspWalletListItem).email || '—' }}</Text>
      </Box>
    </template>

    <!-- WALLET ADDRESS -->
    <template #cell-walletAddress="{ row }">
      <Text type="body-sm" color="muted">
        {{
          (row as VaspWalletListItem).walletAddress
            ? truncate((row as VaspWalletListItem).walletAddress, ADDRESS_TRUNCATE_AT)
            : '—'
        }}
      </Text>
    </template>

    <!-- NETWORK -->
    <template #cell-blockchainNetwork="{ row }">
      <Text type="body-sm">{{ (row as VaspWalletListItem).blockchainNetwork || '—' }}</Text>
    </template>

    <!-- VALIDATION STATUS -->
    <template #cell-walletStatus="{ row }">
      <StatusBadge
        :color="cryptoWalletStatusColor((row as VaspWalletListItem).walletStatus)"
        :label="formatKey((row as VaspWalletListItem).walletStatus)"
      />
    </template>

    <!-- CREATED AT -->
    <template #cell-createdAt="{ row }">
      <Text type="body-sm">{{ formatCreatedAgo((row as VaspWalletListItem).createdAt) }}</Text>
    </template>
  </DataTableV2>
</template>
